import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Router as RouterIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ReceiptLong as ReceiptIcon,
  Dns as DnsIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

const SuperAdminDashboard = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [stats, setStats] = useState({
    totalTenants: 0,
    activeTenants: 0,
    suspendedTenants: 0,
    totalClients: 0,
    totalRouters: 0,
    monthlyRevenue: 0
  });
  const [tenants, setTenants] = useState([]);
  const [globalRouters, setGlobalRouters] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para modal de nuevo inquilino
  const [openNewModal, setOpenNewModal] = useState(false);
  const [newTenant, setNewTenant] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_phone: '',
    plan_type: 'pro',
    max_clients: 500,
    max_routers: 5,
    currency: 'PEN'
  });
  const [submitting, setSubmitting] = useState(false);

  // Estados para modal de edición
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  // Estados para modal de pago de suscripción
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    tenant_id: '',
    amount: '',
    payment_method: 'transfer',
    billing_period_months: 1,
    transaction_ref: ''
  });

  // Estados para modal de eliminación de inquilino
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState(null);

  const handleDeleteTenant = async () => {
    if (!tenantToDelete) return;
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/superadmin/tenants/${tenantToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setSuccess(data.message);
      setOpenDeleteModal(false);
      setTenantToDelete(null);
      fetchSuperAdminData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchSuperAdminData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const [statsRes, tenantsRes, routersRes, subsRes] = await Promise.all([
        fetch('/api/superadmin/stats', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/superadmin/tenants', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/superadmin/global-routers', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/superadmin/subscriptions', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }

      if (tenantsRes.ok) {
        const tenantsData = await tenantsRes.json();
        setTenants(tenantsData.tenants);
      }

      if (routersRes.ok) {
        const routersData = await routersRes.json();
        setGlobalRouters(routersData.routers);
      }

      if (subsRes.ok) {
        const subsData = await subsRes.json();
        setSubscriptions(subsData.subscriptions);
      }
    } catch (err) {
      console.error('Error al cargar datos SuperAdmin:', err);
      setError('Error de conexión al cargar el panel de SuperAdmin');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuperAdminData();
  }, [fetchSuperAdminData]);

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/superadmin/tenants', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTenant)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al crear la empresa WISP');

      setSuccess(data.message);
      setOpenNewModal(false);
      setNewTenant({
        name: '',
        slug: '',
        admin_email: '',
        admin_phone: '',
        plan_type: 'pro',
        max_clients: 500,
        max_routers: 5,
        currency: 'PEN'
      });
      fetchSuperAdminData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (tenantId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/superadmin/tenants/${tenantId}/toggle-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setSuccess(data.message);
      fetchSuperAdminData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    if (!selectedTenant) return;
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/superadmin/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedTenant)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setSuccess(data.message);
      setOpenEditModal(false);
      fetchSuperAdminData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordSubscriptionPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/superadmin/subscriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al registrar pago de suscripción');

      setSuccess(data.message);
      setOpenPaymentModal(false);
      setPaymentData({
        tenant_id: '',
        amount: '',
        payment_method: 'transfer',
        billing_period_months: 1,
        transaction_ref: ''
      });
      fetchSuperAdminData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Columnas para Tabla de Inquilinos WISPs
  const tenantColumns = [
    {
      field: 'name',
      headerName: 'Empresa WISP',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="primary">
            {params.row.slug}.yinowisp.com
          </Typography>
        </Box>
      )
    },
    {
      field: 'admin_email',
      headerName: 'Contacto Admin',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.admin_email}</Typography>
          {params.row.admin_phone && (
            <Typography variant="caption" color="textSecondary">{params.row.admin_phone}</Typography>
          )}
        </Box>
      )
    },
    {
      field: 'plan_type',
      headerName: 'Plan SaaS',
      width: 140,
      renderCell: (params) => {
        let color = 'default';
        if (params.value === 'enterprise') color = 'secondary';
        if (params.value === 'pro') color = 'primary';
        if (params.value === 'emprendedor') color = 'info';

        return (
          <Chip
            label={(params.value || 'pro').toUpperCase()}
            color={color}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    {
      field: 'usage_clients',
      headerName: 'Clientes (Uso / Límite)',
      width: 170,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.current_clients_count || 0} / {params.row.max_clients}
        </Typography>
      )
    },
    {
      field: 'usage_routers',
      headerName: 'Routers (Uso / Límite)',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.row.current_routers_count || 0} / {params.row.max_routers}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'active' ? 'ACTIVO' : 'SUSPENDIDO'}
          color={params.value === 'active' ? 'success' : 'error'}
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 190,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Editar Configuración WISP">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                setSelectedTenant(params.row);
                setOpenEditModal(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Registrar Pago Mensual">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                setPaymentData(prev => ({ ...prev, tenant_id: params.row.id }));
                setOpenPaymentModal(true);
              }}
            >
              <MoneyIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={params.row.status === 'active' ? 'Suspender WISP' : 'Reactivar WISP'}>
            <IconButton
              size="small"
              color={params.row.status === 'active' ? 'warning' : 'success'}
              onClick={() => handleToggleStatus(params.row.id)}
            >
              {params.row.status === 'active' ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {params.row.id !== '00000000-0000-0000-0000-000000000001' && (
            <Tooltip title="Eliminar Empresa WISP">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setTenantToDelete(params.row);
                  setOpenDeleteModal(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  // Columnas para Monitor Global de Routers
  const routerColumns = [
    {
      field: 'name',
      headerName: 'Nombre del Router',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="subtitle2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'tenant_name',
      headerName: 'Empresa WISP Propietaria',
      flex: 1.2,
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">{params.value || 'YinoNet WISP Principal'}</Typography>
          <Typography variant="caption" color="textSecondary">{params.row.tenant_slug || 'yinonet'}</Typography>
        </Box>
      )
    },
    {
      field: 'ip',
      headerName: 'IP de Conexión VPN',
      width: 170,
      renderCell: (params) => (
        <Chip
          label={`${params.value}:${params.row.api_port || 8728}`}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
        />
      )
    },
    {
      field: 'api_type',
      headerName: 'Tipo de API',
      width: 130,
      renderCell: (params) => (
        <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
          {params.value || 'classic'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Estado Túnel',
      width: 140,
      renderCell: (params) => (
        <Chip
          label={params.value === 'online' ? '🟢 ONLINE' : '🔴 OFFLINE'}
          color={params.value === 'online' ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      )
    }
  ];

  // Columnas para Suscripciones SaaS
  const subscriptionColumns = [
    {
      field: 'tenant_name',
      headerName: 'Empresa WISP',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="subtitle2" fontWeight="bold">
          {params.value} ({params.row.tenant_slug})
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Monto Recaudado',
      width: 150,
      renderCell: (params) => (
        <Typography variant="subtitle2" fontWeight="800" color="success.main">
          S/ {parseFloat(params.value || 0).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'payment_method',
      headerName: 'Método',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'billing_period_start',
      headerName: 'Periodo de Cobertura',
      flex: 1.5,
      minWidth: 220,
      renderCell: (params) => (
        <Typography variant="caption" color="textSecondary">
          {new Date(params.value).toLocaleDateString()} — {new Date(params.row.billing_period_end).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'created_at',
      headerName: 'Fecha de Registro',
      width: 160,
      renderCell: (params) => (
        <Typography variant="caption">
          {new Date(params.value).toLocaleString()}
        </Typography>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="800" color="primary.main" gutterBottom>
            ☁️ YinoWISP SaaS — Control SuperAdmin
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Gestión global de empresas WISP inquilinas, suscripciones y recursos del sistema.
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSuperAdminData}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenNewModal(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', px: 3 }}
          >
            Nueva Empresa WISP
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

      {/* KPI Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary" fontWeight="bold">EMPRESAS WISPS</Typography>
                <BusinessIcon color="primary" />
              </Box>
              <Typography variant="h4" fontWeight="800">{stats.activeTenants} / {stats.totalTenants}</Typography>
              <Typography variant="caption" color="textSecondary">WISPs activas en plataforma</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary" fontWeight="bold">TOTAL CLIENTES WISP</Typography>
                <PeopleIcon color="success" />
              </Box>
              <Typography variant="h4" fontWeight="800">{stats.totalClients}</Typography>
              <Typography variant="caption" color="textSecondary">Clientes finales administrados</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary" fontWeight="bold">ROUTERS CONECTADOS</Typography>
                <RouterIcon color="warning" />
              </Box>
              <Typography variant="h4" fontWeight="800">{stats.totalRouters}</Typography>
              <Typography variant="caption" color="textSecondary">Nodos MikroTik en producción</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="textSecondary" fontWeight="bold">INGRESOS SAAS (MRR)</Typography>
                <MoneyIcon color="secondary" />
              </Box>
              <Typography variant="h4" fontWeight="800" color="secondary.main">S/ {stats.monthlyRevenue.toFixed(2)}</Typography>
              <Typography variant="caption" color="textSecondary">Mensualidad recaudada este mes</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Navegación por Pestañas */}
      <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, bgcolor: '#ffffff' }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newIdx) => setTabIndex(newIdx)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab icon={<BusinessIcon />} iconPosition="start" label="Empresas WISP (Inquilinos)" sx={{ fontWeight: 'bold' }} />
          <Tab icon={<DnsIcon />} iconPosition="start" label="Monitor Global de Routers VPN" sx={{ fontWeight: 'bold' }} />
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Pagos & Suscripciones SaaS" sx={{ fontWeight: 'bold' }} />
        </Tabs>

        {/* Pestaña 1: Empresas WISPs */}
        {tabIndex === 0 && (
          <Box sx={{ height: 480, width: '100%' }}>
            <DataGrid
              rows={tenants}
              columns={tenantColumns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{ border: 'none' }}
            />
          </Box>
        )}

        {/* Pestaña 2: Monitor Global de Routers */}
        {tabIndex === 1 && (
          <Box sx={{ height: 480, width: '100%' }}>
            <DataGrid
              rows={globalRouters}
              columns={routerColumns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{ border: 'none' }}
            />
          </Box>
        )}

        {/* Pestaña 3: Pagos y Suscripciones SaaS */}
        {tabIndex === 2 && (
          <Box sx={{ height: 480, width: '100%' }}>
            <DataGrid
              rows={subscriptions}
              columns={subscriptionColumns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              sx={{ border: 'none' }}
            />
          </Box>
        )}
      </Paper>

      {/* Diálogo Nueva Empresa WISP */}
      <Dialog open={openNewModal} onClose={() => setOpenNewModal(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleCreateTenant}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>🏢 Registrar Nueva Empresa WISP</DialogTitle>
          <DialogContent divider sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="Nombre Comercial de la Empresa WISP"
              value={newTenant.name}
              onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
              required
              placeholder="Ej: WISP Alfa Soluciones"
            />

            <TextField
              fullWidth
              label="Subdominio / Slug Único"
              value={newTenant.slug}
              onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })}
              required
              placeholder="Ej: alfa (generará alfa.yinowisp.com)"
              helperText="Se utilizará para acceder al panel propio del WISP"
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Correo del Administrador WISP"
                  type="email"
                  value={newTenant.admin_email}
                  onChange={(e) => setNewTenant({ ...newTenant, admin_email: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono WhatsApp"
                  value={newTenant.admin_phone}
                  onChange={(e) => setNewTenant({ ...newTenant, admin_phone: e.target.value })}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Plan SaaS"
                  value={newTenant.plan_type}
                  onChange={(e) => setNewTenant({ ...newTenant, plan_type: e.target.value })}
                >
                  <MenuItem value="emprendedor">Emprendedor</MenuItem>
                  <MenuItem value="pro">Profesional</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Límite Clientes"
                  value={newTenant.max_clients}
                  onChange={(e) => setNewTenant({ ...newTenant, max_clients: parseInt(e.target.value) })}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Límite Routers"
                  value={newTenant.max_routers}
                  onChange={(e) => setNewTenant({ ...newTenant, max_routers: parseInt(e.target.value) })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setOpenNewModal(false)} disabled={submitting}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              sx={{ borderRadius: 2, fontWeight: 'bold' }}
            >
              {submitting ? 'Guardando...' : 'Crear Inquilino WISP'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo Editar Empresa WISP */}
      {selectedTenant && (
        <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
          <form onSubmit={handleUpdateTenant}>
            <DialogTitle sx={{ fontWeight: 'bold' }}>⚙️ Editar Empresa WISP — {selectedTenant.name}</DialogTitle>
            <DialogContent divider sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                fullWidth
                label="Nombre Comercial"
                value={selectedTenant.name}
                onChange={(e) => setSelectedTenant({ ...selectedTenant, name: e.target.value })}
                required
              />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    select
                    label="Plan SaaS"
                    value={selectedTenant.plan_type}
                    onChange={(e) => setSelectedTenant({ ...selectedTenant, plan_type: e.target.value })}
                  >
                    <MenuItem value="emprendedor">Emprendedor</MenuItem>
                    <MenuItem value="pro">Profesional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Límite Clientes"
                    value={selectedTenant.max_clients}
                    onChange={(e) => setSelectedTenant({ ...selectedTenant, max_clients: parseInt(e.target.value) })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Límite Routers"
                    value={selectedTenant.max_routers}
                    onChange={(e) => setSelectedTenant({ ...selectedTenant, max_routers: parseInt(e.target.value) })}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button onClick={() => setOpenEditModal(false)} disabled={submitting}>Cancelar</Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ borderRadius: 2, fontWeight: 'bold' }}
              >
                {submitting ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      {/* Diálogo Registrar Pago de Suscripción SaaS */}
      <Dialog open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} maxWidth="xs" fullWidth>
        <form onSubmit={handleRecordSubscriptionPayment}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>💳 Registrar Pago de Suscripción WISP</DialogTitle>
          <DialogContent divider sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              select
              label="Empresa WISP Inquilina"
              value={paymentData.tenant_id}
              onChange={(e) => setPaymentData({ ...paymentData, tenant_id: e.target.value })}
              required
            >
              {tenants.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              type="number"
              label="Monto Pagado (S/)"
              value={paymentData.amount}
              onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
              required
            />

            <TextField
              fullWidth
              select
              label="Método de Pago"
              value={paymentData.payment_method}
              onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
            >
              <MenuItem value="transfer">Transferencia Bancaria</MenuItem>
              <MenuItem value="yape">Yape / Plin</MenuItem>
              <MenuItem value="mercadopago">Mercado Pago</MenuItem>
              <MenuItem value="cash">Efectivo</MenuItem>
            </TextField>

            <TextField
              fullWidth
              type="number"
              label="Meses de Cobertura"
              value={paymentData.billing_period_months}
              onChange={(e) => setPaymentData({ ...paymentData, billing_period_months: e.target.value })}
              required
            />

            <TextField
              fullWidth
              label="Referencia / Nº Operación"
              value={paymentData.transaction_ref}
              onChange={(e) => setPaymentData({ ...paymentData, transaction_ref: e.target.value })}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setOpenPaymentModal(false)} disabled={submitting}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <MoneyIcon />}
              sx={{ borderRadius: 2, fontWeight: 'bold' }}
            >
              {submitting ? 'Procesando...' : 'Registrar Pago & Activar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo Confirmar Eliminación de Empresa WISP */}
      {tenantToDelete && (
        <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" /> ¿Eliminar Empresa WISP?
          </DialogTitle>
          <DialogContent divider sx={{ pt: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {tenantToDelete.name} ({tenantToDelete.slug}.yinowisp.com)
            </Typography>
            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
              ⚠️ Esta acción eliminará permanentemente la empresa WISP, sus usuarios, clientes, facturas, planes y configuraciones asociadas. Esta acción NO se puede deshacer.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenDeleteModal(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteTenant}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              sx={{ borderRadius: 2, fontWeight: 'bold' }}
            >
              {submitting ? 'Eliminando...' : 'Eliminar Definitivamente'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default SuperAdminDashboard;
